import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ComponentScore {
  componentId: string;
  name: string;
  dimension: string;
  weight: number;
  score: number;
  feedback: string;
}

export interface ProjectEvaluationRow {
  id: string;
  project_id: string;
  status: string;
  total_score: number | null;
  classification: string | null;
  component_scores: ComponentScore[] | null;
  general_comments: string | null;
  suggestions: string[] | null;
  rubric_answers: Record<string, string> | null;
  evaluated_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

export interface EvaluationWithProject {
  evaluation: ProjectEvaluationRow | null;
  project: {
    id: string;
    name: string;
    status: string | null;
    submitted: boolean | null;
    submitted_at: string | null;
    created_at: string;
    problem: string | null;
    target_audience: string | null;
    data_used: string | null;
    solution_description: string | null;
    solution_type: number | null;
    kpi_name: string | null;
    kpi_current_value: string | null;
    kpi_target_value: string | null;
    cohort_id: string | null;
    user_id: string;
    rubric_answers: Record<string, string> | null;
    file_url: string | null;
  };
  participant: {
    id: string;
    full_name: string;
    company: string | null;
    area: string | null;
    email: string;
  } | null;
  cohort_name: string | null;
}

/**
 * Fetch all submitted projects with their evaluations (if any).
 * Joins with profiles for participant info and cohorts for cohort name.
 */
export function useProjectEvaluations(filters?: {
  organizationId?: string;
  cohortId?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: ['project-evaluations', filters],
    queryFn: async () => {
      // 1. Get submitted projects
      let projectsQuery = supabase
        .from('participant_projects')
        .select('*')
        .eq('submitted', true)
        .order('submitted_at', { ascending: false });

      const { data: projects, error: projErr } = await projectsQuery;
      if (projErr) throw projErr;
      if (!projects || projects.length === 0) return [];

      const projectIds = projects.map(p => p.id);
      const userIds = [...new Set(projects.map(p => p.user_id))];
      const cohortIds = [...new Set(projects.map(p => p.cohort_id).filter(Boolean))] as string[];

      // 2. Get evaluations for these projects
      const { data: evaluations, error: evalErr } = await supabase
        .from('project_evaluations')
        .select('*')
        .in('project_id', projectIds);
      if (evalErr) throw evalErr;

      const evalMap = new Map((evaluations || []).map(e => [e.project_id, e]));

      // 3. Get profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, company, area, email, organization_id')
        .in('id', userIds);


      const profileMap = new Map((profiles || []).map(p => [p.id, p]));

      // 4. Get cohorts
      let cohortMap = new Map<string, string>();
      if (cohortIds.length > 0) {
        const { data: cohorts } = await supabase
          .from('cohorts')
          .select('id, name, organization_id')
          .in('id', cohortIds);
        cohortMap = new Map((cohorts || []).map(c => [c.id, c.name]));

        // Filter by organization if needed
        if (filters?.organizationId && filters.organizationId !== 'all') {
          const orgCohortIds = new Set(
            (cohorts || []).filter(c => (c as any).organization_id === filters.organizationId).map(c => c.id)
          );
          // We'll filter projects below
        }
      }

      // 5. Combine
      let results: EvaluationWithProject[] = projects.map(p => {
        const evalRow = evalMap.get(p.id);
        const profile = profileMap.get(p.user_id);
        return {
          evaluation: evalRow ? {
            ...evalRow,
            component_scores: evalRow.component_scores as any,
            suggestions: evalRow.suggestions as any,
            rubric_answers: evalRow.rubric_answers as any,
          } : null,
          project: {
            ...p,
            rubric_answers: (p as any).rubric_answers || null,
          },
          participant: profile ? {
            id: profile.id,
            full_name: profile.full_name,
            company: profile.company,
            area: profile.area,
            email: profile.email,
          } : null,
          cohort_name: p.cohort_id ? cohortMap.get(p.cohort_id) || null : null,
        };
      });

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        results = results.filter(r => {
          const evalStatus = r.evaluation?.status || 'pending';
          return evalStatus === filters.status;
        });
      }

      if (filters?.cohortId && filters.cohortId !== 'all') {
        results = results.filter(r => r.project.cohort_id === filters.cohortId);
      }

      if (filters?.organizationId && filters.organizationId !== 'all') {
        // Filter by cohort's org
        const { data: orgCohorts } = await supabase
          .from('cohorts')
          .select('id')
          .eq('organization_id', filters.organizationId);
        const orgCohortIds = new Set((orgCohorts || []).map(c => c.id));
        results = results.filter(r => r.project.cohort_id && orgCohortIds.has(r.project.cohort_id));
      }

      return results;
    },
  });
}

/**
 * Rubric component ids required by the LIDER-IA evaluator.
 * Kept in sync with `supabase/functions/evaluate-project/index.ts`.
 */
export const LIDER_IA_COMPONENT_IDS = [
  'strategic_alignment', 'expected_roi', 'process_optimization', 'prototype_maturity',
  'data_quality', 'feasibility_complexity', 'scalability_reuse',
  'governance_ethics_security', 'explainability', 'mlops_monitoring',
  'algorithmic_responsibility', 'adoption_change',
] as const;

export interface EvaluationEligibility {
  eligible: boolean;
  missingSubmission: boolean;
  missingRubricCount: number;
  reason: string | null;
}

/**
 * Determines whether a project can be evaluated by the AI.
 * Rule: project must be submitted (or completed) AND have the 12 rubric answers with ≥20 chars.
 */
export function getEvaluationEligibility(project: {
  submitted?: boolean | null;
  status?: string | null;
  rubric_answers?: Record<string, string> | null;
} | null | undefined): EvaluationEligibility {
  if (!project) {
    return { eligible: false, missingSubmission: true, missingRubricCount: 12, reason: 'Proyecto no cargado' };
  }
  const submitted = project.submitted === true || project.status === 'completed';
  const answers = project.rubric_answers || {};
  const missing = LIDER_IA_COMPONENT_IDS.filter(
    (id) => !answers[id] || answers[id].trim().length < 20,
  );
  const eligible = submitted && missing.length === 0;
  let reason: string | null = null;
  if (!submitted && missing.length > 0) {
    reason = `Falta entregable y ${missing.length} respuesta${missing.length === 1 ? '' : 's'} de la rúbrica`;
  } else if (!submitted) {
    reason = 'El proyecto aún no ha sido entregado';
  } else if (missing.length > 0) {
    reason = `Faltan ${missing.length} respuesta${missing.length === 1 ? '' : 's'} de la rúbrica`;
  }
  return { eligible, missingSubmission: !submitted, missingRubricCount: missing.length, reason };
}

/**
 * Evaluate a project using the edge function (calls AI).
 */
export function useEvaluateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const { data, error } = await supabase.functions.invoke('evaluate-project', {
        body: { project_id: projectId },
      });
      if (error) {
        let body: any = null;
        try {
          const ctx: any = (error as any).context;
          if (ctx?.json) {
            body = await ctx.json();
          } else if (ctx instanceof Response) {
            body = await ctx.clone().json();
          } else if (ctx?.body) {
            body = typeof ctx.body === 'string' ? JSON.parse(ctx.body) : ctx.body;
          }
        } catch { /* ignore */ }

        let message = 'No pudimos evaluar el proyecto en este momento. Inténtalo de nuevo.';
        if (body?.error && !/non-?2xx/i.test(body.error)) message = body.error;
        throw new Error(message);
      }
      if (data?.reason === 'not_ready') {
        const missing: string[] = Array.isArray(data.missing) ? data.missing : [];
        const parts: string[] = [];
        if (missing.includes('submission')) parts.push('el participante aún no ha entregado el proyecto');
        if (missing.includes('rubric')) parts.push('no hay ninguna respuesta de la rúbrica');
        throw new Error(parts.length
          ? `Este proyecto aún no está listo para evaluar: ${parts.join(' y ')}.`
          : 'Este proyecto aún no está listo para evaluar.');
      }

      return data;
    },
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['project-evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['project-evaluation', projectId] });
    },
  });
}

/**
 * Mark evaluation as reviewed.
 */
export function useMarkAsReviewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (projectId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('project_evaluations')
        .update({
          status: 'reviewed',
          reviewed_at: new Date().toISOString(),
          reviewed_by: user.id,
        })
        .eq('project_id', projectId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-evaluations'] });
    },
  });
}

/**
 * Get a single project's evaluation.
 */
export function useProjectEvaluation(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-evaluation', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const { data, error } = await supabase
        .from('project_evaluations')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        ...data,
        component_scores: data.component_scores as any as ComponentScore[] | null,
        suggestions: data.suggestions as any as string[] | null,
        rubric_answers: data.rubric_answers as any as Record<string, string> | null,
      } as ProjectEvaluationRow;
    },
    enabled: !!projectId,
  });
}
